import type {Keyword, KeywordSource} from '../model/Article';
import {isJunkDensityKeyword} from './articleMainKeyword';

export {normalizeKeywordPhrase} from './keywordStemDensity';
import {normalizeKeywordPhrase} from './keywordStemDensity';

function cloneCustomRow(item: Keyword): Keyword {
    return {
        keyword: String(item?.keyword || ''),
        checked: !!item?.checked,
        source: 'custom',
        ...(item?.of ? {of: item.of} : {}),
    };
}

export interface SyncKeywordDensityOptions {
    /**
     * Custom-row indexes currently being typed. Those rows stay even when the
     * draft matches a tracked token. Do not pass this after blur/enter — exact
     * duplicates are dropped then (pre-!217), once the input is no longer focused.
     */
    preserveCustomIndexes?: number[];
}

/**
 * Rebuilds the density list:
 * main keyword → extra AI phrases → leftover custom rows.
 * Does not split the main phrase into one keyword per word, and does not
 * generate inflected duplicates. Density uses stems, not extra rows.
 */
export function syncKeywordDensityList(
    allKeywords: Keyword[] | undefined,
    mainKeyword: string,
    _languageCode: string | null | undefined,
    secondaryKeywords: string[] = [],
    options?: SyncKeywordDensityOptions
): Keyword[] {
    const preserve = new Set(options?.preserveCustomIndexes || []);
    const main = String(mainKeyword || '').replace(/\s+/g, ' ').trim();
    const existing = Array.isArray(allKeywords)
        ? allKeywords.map((item, index) => ({
            keyword: item?.source === 'custom' && preserve.has(index)
                ? String(item?.keyword || '')
                : String(item?.keyword || '').replace(/\s+/g, ' ').trim(),
            checked: !!item?.checked,
            source: item?.source,
            of: item?.of,
        }))
        : [];
    const byNorm = new Map<string, Keyword>();
    for (const item of existing) {
        const key = normalizeKeywordPhrase(item.keyword);
        if (key && !byNorm.has(key)) {
            byNorm.set(key, item);
        }
    }
    const used = new Set<string>();
    const next: Keyword[] = [];
    const push = (phrase: string, defaultChecked: boolean, source: KeywordSource, of?: string) => {
        const trimmed = String(phrase || '').replace(/\s+/g, ' ').trim();
        if (source === 'token' || isJunkDensityKeyword(trimmed, main)) {
            return;
        }
        const key = normalizeKeywordPhrase(trimmed);
        if (!key || used.has(key)) {
            return;
        }
        used.add(key);
        const prev = byNorm.get(key);
        next.push({
            keyword: trimmed,
            checked: prev ? prev.checked : defaultChecked,
            source,
            ...(of ? {of} : {}),
        });
    };

    if (!main) {
        for (const item of existing) {
            if (item.source === 'inflection') {
                continue;
            }
            if (item.source === 'custom') {
                next.push(cloneCustomRow(item));
                continue;
            }
            push(item.keyword, item.checked, item.source || 'custom', item.of);
        }
        return densityListsEqual(allKeywords, next) ? (allKeywords || next) : next;
    }

    push(main, true, 'main');

    for (const extra of secondaryKeywords) {
        push(extra, true, 'secondary');
    }

    existing.forEach((item, index) => {
        const keepFocusedCustom = preserve.has(index) && (item.source === 'custom' || !item.source);
        const key = normalizeKeywordPhrase(item.keyword);
        if (item.source === 'inflection') {
            return;
        }
        if (!key) {
            if (item.source === 'custom' || !item.source) {
                next.push(cloneCustomRow(item));
            }
            return;
        }
        if (item.source === 'token' || isJunkDensityKeyword(item.keyword, main)) {
            if (keepFocusedCustom && item.source !== 'token') {
                next.push(cloneCustomRow(item));
            }
            return;
        }
        if (used.has(key)) {
            if (keepFocusedCustom) {
                next.push(cloneCustomRow(item));
            }
            return;
        }
        if (item.source === 'custom' || !item.source) {
            used.add(key);
            next.push(cloneCustomRow(item));
            return;
        }
        push(item.keyword, item.checked, item.source, item.of);
    });
    return densityListsEqual(allKeywords, next) ? (allKeywords || next) : next;
}

/** True when `phrase` (e.g. a focused draft) matches another density-list row. */
export function phraseDuplicatesTracked(
    phrase: string,
    keywords: Keyword[] | undefined,
    excludeIndex: number
): boolean {
    const key = normalizeKeywordPhrase(phrase);
    if (!key) {
        return false;
    }
    return (keywords || []).some((other, otherIndex) =>
        otherIndex !== excludeIndex && normalizeKeywordPhrase(other.keyword) === key
    );
}

/** True when a custom add-row repeats a phrase already tracked in the density list. */
export function customRowDuplicatesTracked(
    keywords: Keyword[] | undefined,
    index: number
): boolean {
    const list = keywords || [];
    const item = list[index];
    if (!item || (item.source && item.source !== 'custom')) {
        return false;
    }
    return phraseDuplicatesTracked(item.keyword, list, index);
}

export function densityListsEqual(a: Keyword[] | undefined, b: Keyword[] | undefined): boolean {
    const left = a || [];
    const right = b || [];
    if (left.length !== right.length) {
        return false;
    }
    return left.every((item, index) => {
        const other = right[index];
        return other
            && item.keyword === other.keyword
            && item.checked === other.checked
            && (item.source || '') === (other.source || '')
            && (item.of || '') === (other.of || '');
    });
}
